'use client';

import { useEffect, useState } from 'react';
import { useCartStore } from '@/store/cart-store';
import { countryShippingGross } from '@/lib/commerce/rates-fallback';

/**
 * Het landtarief (bruto, incl. btw) zoals de kassa het rekent, voor een
 * clientcomponent: uit /api/countries/{code}, dat getCountryRates gebruikt
 * (Country-tabel, fallback alleen zonder databaserij). Is het gekozen land van
 * de winkelwagen al geladen, dan zonder extra aanvraag. Tot het antwoord
 * binnen is: de fallback van DAT land (nooit NL voor een ander land).
 */
const cache = new Map<string, Promise<number | null>>();

function fetchShippingCost(code: string): Promise<number | null> {
  let request = cache.get(code);
  if (!request) {
    request = fetch(`/api/countries/${code}`)
      .then(async (response) => {
        if (!response.ok) return null;
        const value = Number((await response.json())?.defaultShippingCost);
        return Number.isFinite(value) && value >= 0 ? value : null;
      })
      .catch(() => null);
    // Mislukt: niet bewaren, zodat een volgende mount het opnieuw probeert.
    void request.then((value) => { if (value === null) cache.delete(code); });
    cache.set(code, request);
  }
  return request;
}

export function useCountryShippingCost(countryCode: string | null | undefined): number {
  const code = String(countryCode || 'NL').trim().toUpperCase();
  const selectedCountryCode = useCartStore((state) => state.selectedCountryCode);
  const countryData = useCartStore((state) => state.countryData);
  const storeValue = selectedCountryCode === code ? countryData?.defaultShippingCost : undefined;
  const [fetched, setFetched] = useState<{ code: string; value: number } | null>(null);

  useEffect(() => {
    if (storeValue !== undefined && storeValue !== null) return;
    let cancelled = false;
    void fetchShippingCost(code).then((value) => {
      if (!cancelled && value !== null) setFetched({ code, value });
    });
    return () => { cancelled = true; };
  }, [code, storeValue]);

  return countryShippingGross(code, storeValue ?? (fetched?.code === code ? fetched.value : null));
}
